import * as THREE from 'three'
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js'
import { courseModelOrigin, courseStationPose, courseStationLines, courseStatusColor } from './courseLayout'
import { isCourseCollision } from './courseCollisions'
import { localPlayerPosition } from './localPlayerPosition'

// Render-only batching. Authoring model and PhysX geometry stay unchanged.
function batches(template, origin) {
  template.updateMatrixWorld(true)
  const groups = new Map()
  template.traverse(node => {
    if (!node.isMesh || isCourseCollision(node) || node.name === 'panel') return
    if (Array.isArray(node.material)) throw new Error('course_lod_multi_material_mesh')
    const material = node.material
    const key = material.name
    if (!groups.has(key)) groups.set(key, { material, geometries: [] })
    const geometry = node.geometry.clone()
    for (const name of Object.keys(geometry.attributes)) if (!['position', 'normal', 'uv'].includes(name)) geometry.deleteAttribute(name)
    if (!geometry.getAttribute('uv')) geometry.setAttribute('uv', new THREE.Float32BufferAttribute(new Float32Array(geometry.getAttribute('position').count * 2), 2))
    if (!geometry.getAttribute('normal')) geometry.computeVertexNormals()
    // Match indexing across all parts of one material.
    const flat = geometry.index ? geometry.toNonIndexed() : geometry
    if (flat !== geometry) geometry.dispose()
    flat.applyMatrix4(node.matrixWorld)
    flat.translate(-origin[0], -origin[1], -origin[2])
    groups.get(key).geometries.push(flat)
  })
  return [...groups].map(([name, group]) => {
    const geometry = mergeGeometries(group.geometries)
    for (const part of group.geometries) part.dispose()
    if (!geometry) throw new Error('course_batch_geometry_invalid')
    return { name, material: group.material, geometry }
  })
}

export class CourseStationRenderer {
  constructor(world, template, lodTemplate, stations, marker, canvas = document.createElement('canvas')) {
    this.world = world
    this.stations = stations
    this.group = new THREE.Group()
    this.group.name = 'course-stations-instanced'
    this.ownedMaterials = []
    this.batches = []
    this.lines = []
    this.detail = null
    try {
      const origin = courseModelOrigin(template)
      this.poses = stations.map((station, index) => {
        const pose = courseStationPose(index, marker)
        return { matrix: new THREE.Matrix4().compose(new THREE.Vector3().fromArray(pose.position), new THREE.Quaternion().fromArray(pose.quaternion), new THREE.Vector3(1, 1, 1)), position: new THREE.Vector3().fromArray(pose.position), quaternion: new THREE.Quaternion().fromArray(pose.quaternion) }
      })
      const materials = new Map()
      for (const [tier, model] of [['high', template], ['low', lodTemplate]]) {
        for (const batch of batches(model, origin)) {
          let material = materials.get(batch.name)
          if (!material) {
            const source = tier === 'high' ? batch.material : null
            if (!source) throw new Error('course_lod_material_mismatch')
            material = batch.name === 'course_avatar_status' ? source.clone() : source
            if (batch.name === 'course_avatar_status') { material.color.setRGB(1, 1, 1); world.setupMaterial(material); this.ownedMaterials.push(material) }
            materials.set(batch.name, material)
          }
          const mesh = new THREE.InstancedMesh(batch.geometry, material, Math.max(1, stations.length))
          mesh.count = 0
          mesh.castShadow = false
          mesh.receiveShadow = true
          mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage)
          this.group.add(mesh)
          this.batches.push({ mesh, tier, status: batch.name === 'course_avatar_status' })
        }
      }
      this.canvas = canvas
      this.columns = 8
      this.rows = Math.max(1, Math.ceil(stations.length / this.columns))
      this.tileWidth = 512
      this.tileHeight = 292
      canvas.width = this.columns * this.tileWidth
      canvas.height = this.rows * this.tileHeight
      this.texture = new THREE.CanvasTexture(canvas)
      this.texture.colorSpace = THREE.SRGBColorSpace
      this.texture.generateMipmaps = false
      this.texture.minFilter = THREE.LinearFilter
      this.texture.magFilter = THREE.LinearFilter
      if (world.graphics?.renderer) {
        this.tileCanvas = document.createElement('canvas')
        this.tileCanvas.width = this.tileWidth; this.tileCanvas.height = this.tileHeight
        this.tileTexture = new THREE.CanvasTexture(this.tileCanvas)
        this.tileTexture.colorSpace = THREE.SRGBColorSpace
        this.tileTexture.generateMipmaps = false
        this.tilePosition = new THREE.Vector2()
      }
      const panel = template.getObjectByName('panel')
      if (!panel?.isMesh) throw new Error('course_panel_missing')
      const geometry = panel.geometry.clone()
      geometry.applyMatrix4(panel.matrixWorld)
      geometry.translate(-origin[0], -origin[1], -origin[2])
      const offsets = new Float32Array(Math.max(1, stations.length) * 2)
      for (let i = 0; i < stations.length; i++) { offsets[i * 2] = (i % this.columns) / this.columns; offsets[i * 2 + 1] = 1 - (Math.floor(i / this.columns) + 1) / this.rows }
      geometry.setAttribute('courseAtlasOffset', new THREE.InstancedBufferAttribute(offsets, 2))
      const material = new THREE.MeshBasicMaterial({ map: this.texture, side: THREE.DoubleSide, toneMapped: false })
      material.onBeforeCompile = shader => {
        shader.vertexShader = 'attribute vec2 courseAtlasOffset; varying vec2 courseAtlasUv;\n' + shader.vertexShader
        shader.vertexShader = shader.vertexShader.replace('#include <uv_vertex>', `#include <uv_vertex>\ncourseAtlasUv = vec2(1.0 - clamp(uv.x, 0.002, 0.998), clamp(uv.y, 0.004, 0.996)) * vec2(${1 / this.columns}, ${1 / this.rows}) + courseAtlasOffset;`)
        shader.fragmentShader = 'varying vec2 courseAtlasUv;\n' + shader.fragmentShader
        shader.fragmentShader = shader.fragmentShader.replace('#include <map_fragment>', 'diffuseColor *= texture2D(map, courseAtlasUv);')
      }
      material.customProgramCacheKey = () => `course-atlas-${this.rows}`
      this.ownedMaterials.push(material)
      this.panels = new THREE.InstancedMesh(geometry, material, Math.max(1, stations.length))
      this.panels.count = stations.length
      for (let i = 0; i < stations.length; i++) this.panels.setMatrixAt(i, this.poses[i].matrix)
      this.panels.instanceMatrix.needsUpdate = true
      this.panels.computeBoundingSphere()
      this.group.add(this.panels)
      geometry.computeBoundingBox()
      const center = geometry.boundingBox.getCenter(new THREE.Vector3())
      this.targets = stations.map((station, index) => ({ station, position: center.clone().applyMatrix4(this.poses[index].matrix), quaternion: this.poses[index].quaternion.clone().multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI)) }))
      this.updateStations(stations)
      this.updateDistance(localPlayerPosition(world))
    } catch (error) { this.dispose(); throw error }
  }
  updateStations(stations) {
    this.stations = stations
    const ctx = this.canvas.getContext('2d')
    let dirty = false
    stations.forEach((station, index) => {
      this.targets[index].station = station
      const lines = courseStationLines(station)
      if (this.lines[index] && lines.length === this.lines[index].length && lines.every((line, row) => line === this.lines[index][row])) return
      const x = (index % this.columns) * this.tileWidth, y = Math.floor(index / this.columns) * this.tileHeight
      ctx.fillStyle = '#111827'; ctx.fillRect(x, y, this.tileWidth, this.tileHeight)
      ctx.fillStyle = '#ffffff'; ctx.font = '16px sans-serif'
      lines.forEach((line, row) => ctx.fillText(line.slice(0, 100), x + 12, y + 29 + row * 37, this.tileWidth - 24))
      this.lines[index] = lines
      if (this.atlasInitialized && this.tileTexture) {
        this.tileCanvas.getContext('2d').drawImage(this.canvas, x, y, this.tileWidth, this.tileHeight, 0, 0, this.tileWidth, this.tileHeight)
        this.tilePosition.set(x, this.canvas.height - y - this.tileHeight)
        // Three r173 uploads this CPU canvas with texSubImage2D: no full atlas refresh.
        this.world.graphics.renderer.copyTextureToTexture(this.tileTexture, this.texture, null, this.tilePosition)
      }
      dirty = true
    })
    if (dirty && (!this.atlasInitialized || !this.tileTexture)) this.texture.needsUpdate = true
    this.atlasInitialized = true
    if (this.detail) this.applyInstances()
  }
  updateDistance(position) {
    const distances = this.poses.map(pose => pose.position.distanceToSquared(position))
    const next = distances.map((distance, index) => distance <= (this.detail?.[index] ? 81 : 49))
    if (this.world.xr?.session) {
      const nearest = distances.map((distance, index) => ({ distance, index })).filter(value => next[value.index]).sort((a, b) => a.distance - b.distance || a.index - b.index)
      for (const value of nearest.slice(4)) next[value.index] = false
    }
    if (this.detail && next.every((high, index) => high === this.detail[index])) return
    this.detail = next
    this.applyInstances()
  }
  applyInstances() {
    const color = new THREE.Color()
    for (const { mesh, tier, status } of this.batches) {
      let count = 0
      for (let i = 0; i < this.stations.length; i++) {
        if (this.detail[i] !== (tier === 'high')) continue
        mesh.setMatrixAt(count, this.poses[i].matrix)
        if (status) mesh.setColorAt(count, color.setRGB(...courseStatusColor(this.stations[i].alarmState)))
        count++
      }
      mesh.count = count
      mesh.visible = count > 0
      mesh.instanceMatrix.needsUpdate = true
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true
      mesh.computeBoundingSphere()
    }
  }
  dispose() {
    this.group.removeFromParent()
    for (const { mesh } of this.batches) { mesh.dispose(); mesh.geometry.dispose() }
    this.panels?.dispose(); this.panels?.geometry.dispose()
    for (const material of this.ownedMaterials) material.dispose()
    this.texture?.dispose()
    this.tileTexture?.dispose()
    if (this.tileCanvas) this.tileCanvas.width = this.tileCanvas.height = 1
    if (this.canvas) this.canvas.width = this.canvas.height = 1
  }
}
